/**
 * Cognito hosted-UI sign-in for the enterprise SSO lane (EPT-3938).
 *
 * The app's primary lane is Auth0 via `@auth0/auth0-react`. Cognito serves the
 * domains listed in COGNITO_EMAIL_DOMAINS, at signin.{domain}, and cannot use
 * the Auth0 SDK. This module is that second lane: authorization-code + PKCE,
 * token exchange, and a session the app context can read.
 *
 * The tokens are claim-identical to Auth0's apart from `iss` and `aud`, so
 * nothing downstream branches on which lane issued them.
 */

import { cognitoClientId, cognitoDomain } from '../config';
import { isLeavingForLogout } from './appLogin';
import { decodeJwtSegment } from './localAuth';

const VERIFIER_KEY = 'eptai_cognito_pkce_verifier';
const STATE_KEY = 'eptai_cognito_state';
const RETURN_TO_KEY = 'eptai_cognito_return_to';

// sessionStorage, matching the Salesforce Canvas lane: a refresh token in
// localStorage survives every XSS for 30 days. A new tab starts with empty
// sessionStorage, so it asks the open tabs for their session over
// SESSION_CHANNEL (the Cognito stand-in for Auth0's silent sign-in). The
// session still ends when the last tab closes.
export const COGNITO_ID_TOKEN_KEY = 'eptai_cognito_id_token';
export const COGNITO_ACCESS_TOKEN_KEY = 'eptai_cognito_access_token';
export const COGNITO_REFRESH_TOKEN_KEY = 'eptai_cognito_refresh_token';

export type CognitoTokens = {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
};

type TokenEndpointResponse = {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  error?: string;
  error_description?: string;
};

type CognitoLoginExchange = {
  key: string;
  promise: Promise<CognitoTokens>;
};

// React.StrictMode deliberately re-runs effects in development. Keep the
// exchange for one callback URL stable so a remount cannot spend its code a
// second time (and callers that arrive after it settles receive the result).
let loginExchange: CognitoLoginExchange | null = null;

function _loginExchangeKey(code: string, state: string): string {
  return JSON.stringify([code, state]);
}

function _base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach(b => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function _randomUrlSafe(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return _base64UrlEncode(bytes);
}

/**
 * S256 PKCE challenge for a verifier.
 *
 * Example: verifier 'abc' -> base64url(sha256('abc'))
 */
async function _pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return _base64UrlEncode(new Uint8Array(digest));
}

/** The registered callback. Must match a Cognito callback URL exactly. */
export function cognitoRedirectUri(): string {
  return `${window.location.origin}/login`;
}

/**
 * Where the hosted UI should send this user.
 *
 * `idp_identifier` is the email domain, which Cognito resolves to the matching
 * identity provider server-side and redirects straight to — its own page never
 * renders, so the user is not asked for their email a second time. This is the
 * Cognito equivalent of Auth0 home-realm discovery from `login_hint`.
 *
 * A returning user has no email to give; pass the provider name saved from
 * their last sign-in instead (EPD-79), which Cognito follows the same way.
 *
 * Example: 'ada@ept-ai.com' -> '...&idp_identifier=ept-ai.com&login_hint=ada%40ept-ai.com'
 * Example: { identityProvider: 'Novanta-account-id-7760' } -> '...&identity_provider=Novanta-account-id-7760'
 */
export async function buildCognitoAuthorizeUrl(
  login: string | { identityProvider: string },
  returnTo?: string
): Promise<string | null> {
  const domain = cognitoDomain();
  const clientId = cognitoClientId();
  const idpParams: Record<string, string> =
    typeof login === 'string'
      ? { idp_identifier: login.split('@')[1] ?? '', login_hint: login }
      : { identity_provider: login.identityProvider };
  if (!domain || !clientId || !Object.values(idpParams)[0]) {
    return null;
  }

  const verifier = _randomUrlSafe(32);
  const state = _randomUrlSafe(16);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  if (returnTo) {
    sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  } else {
    sessionStorage.removeItem(RETURN_TO_KEY);
  }

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: cognitoRedirectUri(),
    state,
    code_challenge: await _pkceChallenge(verifier),
    code_challenge_method: 'S256',
    ...idpParams,
  });
  return `https://${domain}/oauth2/authorize?${params.toString()}`;
}

/**
 * True when the current URL is Cognito handing back an authorization code.
 *
 * Requires a `state` matching the one we stored, so a stray `?code=` from
 * anywhere else is ignored rather than sent to the token endpoint.
 */
export function isCognitoCallback(search: string = window.location.search): boolean {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const state = params.get('state');
  if (!code || !state) {
    return false;
  }
  return (
    loginExchange?.key === _loginExchangeKey(code, state) ||
    state === sessionStorage.getItem(STATE_KEY)
  );
}

export function readCognitoSession(): CognitoTokens | null {
  const idToken = sessionStorage.getItem(COGNITO_ID_TOKEN_KEY);
  const accessToken = sessionStorage.getItem(COGNITO_ACCESS_TOKEN_KEY);
  if (!idToken || !accessToken) {
    return null;
  }
  return {
    idToken,
    accessToken,
    refreshToken: sessionStorage.getItem(COGNITO_REFRESH_TOKEN_KEY) || undefined,
  };
}

export function storeCognitoSession(tokens: CognitoTokens): void {
  sessionStorage.setItem(COGNITO_ID_TOKEN_KEY, tokens.idToken);
  sessionStorage.setItem(COGNITO_ACCESS_TOKEN_KEY, tokens.accessToken);
  if (tokens.refreshToken) {
    sessionStorage.setItem(COGNITO_REFRESH_TOKEN_KEY, tokens.refreshToken);
  }
}

export function clearCognitoSession(): void {
  [
    COGNITO_ID_TOKEN_KEY,
    COGNITO_ACCESS_TOKEN_KEY,
    COGNITO_REFRESH_TOKEN_KEY,
    VERIFIER_KEY,
    STATE_KEY,
    RETURN_TO_KEY,
  ].forEach(key => sessionStorage.removeItem(key));
}

// Open tabs hand their session to a new tab on this channel, and a logout in
// one tab signs the others out so none can hand a signed-out session back.
// Every page on app.ept.ai can use the channel, so it reaches the tokens of
// every open tab, not only its own. See PR #899 for why that is accepted.
type SessionMessage =
  | { type: 'request' }
  | { type: 'session'; tokens: CognitoTokens }
  | { type: 'logout' };

let sessionChannel: BroadcastChannel | null = null;
let onLogoutInOtherTab = (): void => {};

function _sessionChannel(): BroadcastChannel | null {
  if (!sessionChannel && typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel('eptai_cognito_session');
    channel.addEventListener('message', ({ data }: MessageEvent<SessionMessage>) => {
      if (data?.type === 'logout') {
        clearCognitoSession();
        onLogoutInOtherTab();
      } else if (data?.type === 'request') {
        const tokens = readCognitoSession();
        if (tokens) {
          channel.postMessage({ type: 'session', tokens });
        }
      }
    });
    sessionChannel = channel;
  }
  return sessionChannel;
}

/** Only tokens issued to this tab's app client: not another environment's, not junk. */
function _isOwnClientSession(tokens: CognitoTokens | undefined): tokens is CognitoTokens {
  try {
    const claims = JSON.parse(decodeJwtSegment(tokens!.idToken.split('.')[1])) as { aud?: string };
    return claims.aud === cognitoClientId() && typeof tokens!.accessToken === 'string';
  } catch {
    return false;
  }
}

/**
 * Answer new tabs, and call `onLogout` when another tab logs out (its storage
 * is already cleared by then). Call once at app start.
 */
export function shareCognitoSessionWithNewTabs(onLogout: () => void): void {
  onLogoutInOtherTab = onLogout;
  _sessionChannel();
}

/** Sign every other open tab out. Call before leaving for logout. */
export function broadcastCognitoLogout(): void {
  _sessionChannel()?.postMessage({ type: 'logout' });
}

/**
 * The session an open tab hands over, or null when none answers in 300 ms.
 * Replies for another app client are ignored.
 */
export function requestCognitoSessionFromOtherTabs(): Promise<CognitoTokens | null> {
  const channel = _sessionChannel();
  if (!channel) {
    return Promise.resolve(null);
  }
  return new Promise(resolve => {
    const done = (tokens: CognitoTokens | null): void => {
      channel.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(tokens);
    };
    const onMessage = ({ data }: MessageEvent<SessionMessage>): void => {
      if (data?.type === 'session' && _isOwnClientSession(data.tokens)) {
        done(data.tokens);
      }
    };
    const timer = setTimeout(() => done(null), 300);
    channel.addEventListener('message', onMessage);
    channel.postMessage({ type: 'request' });
  });
}

/** Where to land after a successful exchange. Consumed once. */
export function takeCognitoReturnTo(): string | undefined {
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || undefined;
  sessionStorage.removeItem(RETURN_TO_KEY);
  return returnTo;
}

async function _postToTokenEndpoint(body: URLSearchParams): Promise<TokenEndpointResponse> {
  const response = await fetch(`https://${cognitoDomain()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  return (await response.json()) as TokenEndpointResponse;
}

/**
 * Trade the authorization code for tokens and store the session.
 *
 * No client secret: this is a public client, and PKCE is what proves the
 * exchange came from the browser that started the flow.
 */
export async function completeCognitoLogin(
  search: string = window.location.search
): Promise<CognitoTokens> {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const state = params.get('state');
  if (!code || !state) {
    throw new Error('Sign-in could not be completed. Please try again.');
  }
  const exchangeKey = _loginExchangeKey(code, state);

  if (loginExchange?.key === exchangeKey) {
    return loginExchange.promise;
  }

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  if (!verifier || !expectedState || state !== expectedState) {
    throw new Error('Sign-in could not be completed. Please try again.');
  }

  const promise = (async (): Promise<CognitoTokens> => {
    try {
      const payload = await _postToTokenEndpoint(
        new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: cognitoClientId(),
          code,
          redirect_uri: cognitoRedirectUri(),
          code_verifier: verifier,
        })
      );

      if (!payload.id_token || !payload.access_token) {
        throw new Error(payload.error_description || payload.error || 'Sign-in failed.');
      }

      const tokens: CognitoTokens = {
        idToken: payload.id_token,
        accessToken: payload.access_token,
        refreshToken: payload.refresh_token,
      };
      storeCognitoSession(tokens);
      return tokens;
    } finally {
      // Do not clear a newer sign-in attempt if one was started while this
      // request was in flight.
      if (sessionStorage.getItem(VERIFIER_KEY) === verifier) {
        sessionStorage.removeItem(VERIFIER_KEY);
      }
      if (sessionStorage.getItem(STATE_KEY) === state) {
        sessionStorage.removeItem(STATE_KEY);
      }
    }
  })();

  loginExchange = { key: exchangeKey, promise };
  return promise;
}

/**
 * Refresh with the stored refresh token.
 *
 * Cognito does not return a new refresh token on this grant, so the existing
 * one is carried forward until it expires and the user signs in again.
 */
export async function refreshCognitoSession(): Promise<CognitoTokens | null> {
  const current = readCognitoSession();
  if (!current?.refreshToken) {
    return null;
  }

  try {
    const payload = await _postToTokenEndpoint(
      new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: cognitoClientId(),
        refresh_token: current.refreshToken,
      })
    );
    // Logout started while this was in flight: storing now would put the
    // session back after logout cleared it.
    if (isLeavingForLogout() || !payload.id_token || !payload.access_token) {
      return null;
    }
    const tokens: CognitoTokens = {
      idToken: payload.id_token,
      accessToken: payload.access_token,
      refreshToken: current.refreshToken,
    };
    storeCognitoSession(tokens);
    return tokens;
  } catch (error) {
    console.error('[COGNITO] Refresh failed:', error);
    return null;
  }
}

/**
 * Leave the SPA for the hosted UI.
 *
 * A seam, so the redirect lives with the rest of the Cognito lane and callers
 * do not reach into `window` themselves.
 */
export function redirectToCognito(url: string): void {
  window.location.assign(url);
}

/** Finish the callback with a same-origin navigation to the saved deep link. */
export function redirectAfterCognitoLogin(returnTo: string): void {
  const target = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/';
  window.location.replace(target);
}

/** Hosted-UI logout, which also clears the Cognito session cookie. */
export function cognitoLogoutUrl(returnTo: string): string {
  const params = new URLSearchParams({
    client_id: cognitoClientId(),
    logout_uri: returnTo,
  });
  return `https://${cognitoDomain()}/logout?${params.toString()}`;
}
